import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { useToast } from 'src/components/toast'
import { extractErrorMessage } from 'src/utils/errors'

interface UseDeleteWithConfirmationOptions {
  mutationFn: (id: string) => Promise<unknown>
  queryKey: readonly unknown[]
  onSuccess?: () => void
  /**
   * When `true`, the hook also calls `invalidateQueries({ queryKey })` on
   * success — for consumers whose deletes trigger server-side cascades that the
   * optimistic removal cannot reconcile locally. Defaults to `false`.
   */
  invalidateOnSuccess?: boolean
  /**
   * Toast description shown when the delete succeeds. Omit for no success toast.
   */
  successMessage?: string
}

interface DialogProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  itemName: string
}

type PaginatedCache = { count: number; results: unknown[] }

function hasId(item: unknown): item is { id: string } {
  return typeof item === 'object' && item !== null && 'id' in item
}

function isPaginatedCache(data: unknown): data is PaginatedCache {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as PaginatedCache).results) &&
    typeof (data as PaginatedCache).count === 'number'
  )
}

/**
 * Remove the item with the given `id` from a single cache entry, handling both
 * plain array caches and paginated `{ count, results }` caches. Returns the
 * input unchanged when it is neither shape or the item is not present.
 */
function removeItemFromCache(data: unknown, id: string): unknown {
  if (Array.isArray(data)) {
    return data.filter((item) => !(hasId(item) && item.id === id))
  }
  if (isPaginatedCache(data)) {
    const results = data.results.filter((item) => !(hasId(item) && item.id === id))
    if (results.length === data.results.length) return data
    return { ...data, results, count: data.count - 1 }
  }
  return data
}

export function useDeleteWithConfirmation({
  mutationFn,
  queryKey,
  onSuccess,
  invalidateOnSuccess = false,
  successMessage,
}: UseDeleteWithConfirmationOptions) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [targetId, setTargetId] = useState<string | null>(null)
  const [itemName, setItemName] = useState('')

  const mutation = useMutation({
    mutationFn,
    onMutate: async (id: string) => {
      // Prevent an in-flight refetch from overwriting the optimistic removal.
      await queryClient.cancelQueries({ queryKey })
      // Snapshot every matching cache variant (prefix match) for rollback.
      const previous = queryClient.getQueriesData({ queryKey })
      queryClient.setQueriesData({ queryKey }, (old: unknown) => removeItemFromCache(old, id))
      return { previous }
    },
    onError: (error, _id, context) => {
      // Roll back to the pre-delete snapshot so the removed row reappears.
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      // Surface the backend's own message (e.g. "Cannot remove the last owner
      // of this organisation.") rather than the generic fallback.
      toast({
        description: extractErrorMessage(error, i18next.t('deleteConfirmation.deleteError')),
        type: 'error',
      })
    },
    onSuccess: () => {
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries({ queryKey })
      }
      setTargetId(null)
      setItemName('')
      if (successMessage) {
        toast({ description: successMessage, type: 'success' })
      }
      onSuccess?.()
    },
  })

  const requestDelete = useCallback((id: string, name?: string) => {
    setTargetId(id)
    setItemName(name ?? '')
  }, [])

  const onCancel = useCallback(() => {
    if (mutation.isPending) return
    setTargetId(null)
    setItemName('')
  }, [mutation.isPending])

  const onConfirm = useCallback(() => {
    if (!targetId) return
    mutation.mutate(targetId)
  }, [targetId, mutation])

  const dialogProps: DialogProps = {
    isOpen: targetId !== null,
    onConfirm,
    onCancel,
    isPending: mutation.isPending,
    itemName,
  }

  return { requestDelete, dialogProps }
}
